import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  DISTANCIAS,
  asignarCategoria,
  categoriaPermitida,
  categoriasPermitidas,
} from "@/lib/categorias";
import { crearInscripcion, suscribirInscripcionesAbiertas, type DatosInscripcion } from "@/lib/inscripciones";
import { subirImagenCloudinary } from "@/lib/cloudinary";

// Tallas de camiseta disponibles. Edita esta lista si cambian.
const TALLAS = ["XS", "S", "M", "L", "XL", "XXL"];

const MAX_MB = 5;

const VACIO: DatosInscripcion = {
  nombres: "",
  cedula: "",
  fechaNacimiento: "",
  distancia: "",
  categoria: "",
  sexo: "" as DatosInscripcion["sexo"],
  telefono: "",
  email: "",
  clubProvincia: "",
  tallaCamiseta: "",
  comprobanteUrl: "",
  comentario: "",
};

type Estado = { tipo: "exito" | "error"; texto: string } | null;

export default function InscribirsePage() {
  const [form, setForm] = useState<DatosInscripcion>(VACIO);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [resetKey, setResetKey] = useState(0);
  const [enviando, setEnviando] = useState(false);
  const [estado, setEstado] = useState<Estado>(null);

  // Estado abierto/cerrado controlado desde el panel admin (por defecto abiertas).
  const [abiertas, setAbiertas] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    return suscribirInscripcionesAbiertas(setAbiertas);
  }, []);

  const categoria = asignarCategoria(form.fechaNacimiento);

  let avisoCategoria = "";
  if (categoria && form.distancia) {
    if (categoria === "Sin categoría") {
      avisoCategoria = "Según tu año de nacimiento no calificas en ninguna categoría.";
    } else if (!categoriaPermitida(form.distancia, categoria)) {
      avisoCategoria =
        `La categoría ${categoria} no puede correr ${form.distancia}. ` +
        `En esa distancia participan: ${categoriasPermitidas(form.distancia).join(", ")}.`;
    }
  }

  function actualizar(campo: keyof DatosInscripcion, valor: string) {
    setForm((prev) => ({ ...prev, [campo]: valor }));
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setEstado(null);

    if (!categoria || categoria === "Sin categoría") {
      setEstado({ tipo: "error", texto: "Tu año de nacimiento no califica en ninguna categoría." });
      return;
    }
    if (!categoriaPermitida(form.distancia, categoria)) {
      setEstado({
        tipo: "error",
        texto: `La categoría ${categoria} no puede correr ${form.distancia} (solo: ${categoriasPermitidas(form.distancia).join(", ")}).`,
      });
      return;
    }
    if (!archivo) {
      setEstado({ tipo: "error", texto: "Adjunta la foto del comprobante de pago." });
      return;
    }
    if (!archivo.type.startsWith("image/")) {
      setEstado({ tipo: "error", texto: "El comprobante debe ser una imagen (JPG, PNG, etc.)." });
      return;
    }
    if (archivo.size > MAX_MB * 1024 * 1024) {
      setEstado({ tipo: "error", texto: `La imagen supera ${MAX_MB}MB. Usa una más liviana.` });
      return;
    }

    setEnviando(true);
    try {
      const comprobanteUrl = await subirImagenCloudinary(archivo);
      await crearInscripcion({ ...form, categoria, comprobanteUrl });
      setForm(VACIO);
      setArchivo(null);
      setResetKey((k) => k + 1);
      setEstado({ tipo: "exito", texto: "¡Inscripción enviada! Quedó en revisión. Te contactaremos pronto." });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      setEstado({ tipo: "error", texto: "Error al enviar. Revisa tu conexión e intenta de nuevo." });
    } finally {
      setEnviando(false);
    }
  }

  const inputClass =
    "w-full px-4 py-3 rounded-xl border border-foreground-200 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-500";
  const labelClass = "block text-sm font-semibold text-foreground-800 mb-1";
  const cardClass = "bg-white rounded-2xl shadow-sm p-5 md:p-6";

  // ---------- Inscripciones cerradas ----------
  if (!abiertas) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <header className="bg-white shadow-sm">
          <div className="max-w-3xl mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 font-heading text-lg md:text-xl tracking-wide text-foreground-950">
              <img
                src="https://static.readdy.ai/image/3ac88beca320db2f961147cc19b75475/c966e299d7275505654852a65f959d1a.png"
                alt="Logo Baños Run"
                className="w-9 h-9 rounded-full object-cover"
              />
              BAÑOS RUN 2026
            </Link>
            <Link
              to="/"
              className="px-4 py-2 rounded-full border border-primary-500 text-primary-500 text-sm font-semibold hover:bg-primary-50 transition-colors whitespace-nowrap"
            >
              ← Inicio
            </Link>
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center px-4 py-10 md:py-16">
          <section className="w-full max-w-2xl bg-gradient-to-br from-primary-500 to-primary-700 text-white rounded-3xl p-8 md:p-12 text-center shadow-xl">
            <div className="mx-auto w-24 h-24 bg-white rounded-2xl p-2 shadow-md flex items-center justify-center mb-6">
              <img
                src="/logo-banosrun.png"
                alt="Logo Baños Run 2026"
                className="max-w-full max-h-full object-contain"
              />
            </div>

            <div className="mx-auto w-16 h-16 rounded-full bg-white/15 ring-1 ring-white/30 flex items-center justify-center mb-4">
              <i className="ri-checkbox-circle-line text-4xl" />
            </div>

            <h1 className="font-heading text-4xl md:text-5xl tracking-wide">INSCRIPCIONES TERMINADAS</h1>
            <p className="text-white/90 mt-4 text-base md:text-lg leading-relaxed max-w-lg mx-auto">
              El período de inscripciones para <strong>Baños Run 2026</strong> ha finalizado.
              ¡Gracias a todos los atletas por ser parte de este Campeonato Nacional de Carreras de Calle!
            </p>

            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-6 text-sm font-semibold">
              <span><i className="ri-calendar-line mr-1" /> Sábado 18 de Julio 2026</span>
              <span><i className="ri-time-line mr-1" /> 07H00</span>
              <span><i className="ri-map-pin-line mr-1" /> Parque Montalvo, Baños</span>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
              <Link
                to="/verificar"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white text-primary-600 font-semibold hover:bg-background-50 transition-colors whitespace-nowrap"
              >
                <i className="ri-search-line" /> Verificar mi inscripción
              </Link>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white/15 ring-1 ring-white/30 text-white font-semibold hover:bg-white/25 transition-colors whitespace-nowrap"
              >
                Volver al inicio
              </Link>
            </div>

            <p className="text-white/70 text-sm mt-6">
              ¿Dudas? Escríbenos por WhatsApp al{" "}
              <a href="https://wa.me/593979102532" target="_blank" rel="noopener noreferrer" className="underline font-semibold text-white">
                +593 97 910 2532
              </a>
            </p>
          </section>
        </main>
      </div>
    );
  }

  // ---------- Formulario de inscripción (activo cuando el admin las tiene abiertas) ----------
  return (
    <div className="min-h-screen bg-background-50">
      {/* Encabezado */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center overflow-hidden">
              <img
                src="https://static.readdy.ai/image/3ac88beca320db2f961147cc19b75475/c966e299d7275505654852a65f959d1a.png"
                alt="Logo Baños Run"
                className="w-full h-full object-cover"
              />
            </div>
            <span className="font-heading text-lg md:text-xl tracking-wide text-foreground-950">
              BAÑOS RUN 2026
            </span>
          </Link>
          <Link
            to="/"
            className="px-4 py-2 rounded-full border border-primary-500 text-primary-500 text-sm font-semibold hover:bg-primary-50 transition-colors whitespace-nowrap"
          >
            ← Inicio
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-10 space-y-5">
        {/* Bienvenida + datos del evento */}
        <section className="bg-gradient-to-br from-primary-500 to-primary-700 text-white rounded-2xl p-6 md:p-8">
          {/* Logo oficial */}
          <div className="flex justify-center mb-5">
            <div className="bg-white rounded-2xl p-4 shadow-md">
              <img src="/logo-banosrun.png" alt="Logo Baños Run 2026" className="h-24 md:h-28 w-auto object-contain" />
            </div>
          </div>

          <button
            type="button"
            onClick={() => document.getElementById("datos")?.scrollIntoView({ behavior: "smooth", block: "start" })}
            className="font-heading text-3xl md:text-4xl tracking-wide text-center w-full cursor-pointer hover:underline focus:outline-none"
            title="Ir a llenar el formulario"
          >
            FORMULARIO DE INSCRIPCIÓN
          </button>
          <p className="text-white/90 mt-1 text-center">Baños Run 2026 · Campeonato Nacional de Carreras de Calle</p>
          <p className="text-white/90 mt-3 text-sm leading-relaxed text-center max-w-xl mx-auto">
            Amigos atletas, ¡bienvenidos a Baños Run 2026! Completa tus datos para inscribirte.
            Tu inscripción quedará en revisión hasta confirmar el pago.
          </p>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-4 text-sm font-semibold">
            <span><i className="ri-calendar-line mr-1" /> Sábado 18 de Julio 2026</span>
            <span><i className="ri-time-line mr-1" /> 07H00</span>
            <span><i className="ri-map-pin-line mr-1" /> Parque Montalvo, Baños</span>
            <span><i className="ri-wallet-3-line mr-1" /> $20</span>
          </div>
        </section>

        {/* Afiche oficial del evento */}
        <section className={cardClass}>
          <img
            src="/afiche.png"
            alt="Afiche oficial Baños Run 2026"
            className="w-full rounded-xl"
          />
        </section>

        {/* Kit del corredor */}
        <section className={cardClass}>
          <h2 className="font-heading text-2xl text-foreground-950 mb-3">KIT CORREDOR OFICIAL</h2>
          <p className="text-sm text-foreground-600 mb-3">Con tu inscripción ($20) recibes:</p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-foreground-700">
            <li className="flex items-center gap-2"><i className="ri-medal-line text-primary-500" /> Medalla conmemorativa de finalista</li>
            <li className="flex items-center gap-2"><i className="ri-t-shirt-line text-primary-500" /> Camiseta oficial del evento</li>
            <li className="flex items-center gap-2"><i className="ri-bookmark-line text-primary-500" /> Dorsal de competencia</li>
            <li className="flex items-center gap-2"><i className="ri-timer-flash-line text-primary-500" /> Chip electrónico de cronometraje</li>
            <li className="flex items-center gap-2"><i className="ri-drop-line text-primary-500" /> Hidratación en ruta y recuperación</li>
          </ul>
        </section>

        {/* Categorías informativas */}
        <section className={cardClass}>
          <h2 className="font-heading text-2xl text-foreground-950 mb-2">CATEGORÍAS POR AÑO DE NACIMIENTO</h2>
          <p className="text-sm text-foreground-600 mb-4">
            Tu categoría se asigna automáticamente según tu año de nacimiento, y cada distancia admite
            ciertas categorías:
          </p>

          {/* Qué categorías corre cada distancia */}
          <div className="overflow-x-auto mb-4">
            <table className="w-full text-sm border border-foreground-200">
              <thead>
                <tr className="bg-foreground-50 text-left">
                  <th className="px-3 py-2 border border-foreground-200">Distancia</th>
                  <th className="px-3 py-2 border border-foreground-200">Categorías habilitadas</th>
                </tr>
              </thead>
              <tbody>
                {DISTANCIAS.map((d) => (
                  <tr key={d.nombre}>
                    <td className="px-3 py-2 border border-foreground-200 font-semibold">{d.nombre}</td>
                    <td className="px-3 py-2 border border-foreground-200">{d.categorias.join(", ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Años de nacimiento por categoría */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-foreground-200">
              <thead>
                <tr className="bg-foreground-50 text-left">
                  <th className="px-3 py-2 border border-foreground-200">Categoría</th>
                  <th className="px-3 py-2 border border-foreground-200">Años de nacimiento</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="px-3 py-2 border border-foreground-200 font-semibold">Sub16</td>
                  <td className="px-3 py-2 border border-foreground-200">2011, 2012, 2013</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 border border-foreground-200 font-semibold">Sub18</td>
                  <td className="px-3 py-2 border border-foreground-200">2009, 2010</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 border border-foreground-200 font-semibold">Sub20</td>
                  <td className="px-3 py-2 border border-foreground-200">2007, 2008</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 border border-foreground-200 font-semibold">Adultos</td>
                  <td className="px-3 py-2 border border-foreground-200">2006 en adelante (20 años cumplidos o más)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Mensaje de estado en modal centrado */}
        {estado && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50"
            onClick={() => setEstado(null)}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                  estado.tipo === "exito" ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"
                }`}
              >
                <i className={`text-4xl ${estado.tipo === "exito" ? "ri-check-line" : "ri-error-warning-line"}`} />
              </div>
              <p className={`text-base ${estado.tipo === "exito" ? "text-green-800" : "text-red-800"}`}>
                {estado.texto}
              </p>
              <button
                onClick={() => setEstado(null)}
                className="mt-5 px-6 py-2.5 rounded-full bg-primary-500 text-white font-semibold hover:bg-primary-600 transition-colors"
              >
                Entendido
              </button>
            </div>
          </div>
        )}

        {/* Formulario */}
        <form id="datos" onSubmit={onSubmit} className={`${cardClass} space-y-4 scroll-mt-24`} noValidate>
          <h2 className="font-heading text-2xl text-foreground-950">TUS DATOS</h2>

          <div>
            <label className={labelClass}>Nombres y apellidos completos *</label>
            <input className={inputClass} type="text" required value={form.nombres}
              onChange={(e) => actualizar("nombres", e.target.value)} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Número de cédula *</label>
              <input className={inputClass} type="text" inputMode="numeric" required value={form.cedula}
                onChange={(e) => actualizar("cedula", e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Sexo *</label>
              <select className={inputClass} required value={form.sexo}
                onChange={(e) => actualizar("sexo", e.target.value)}>
                <option value="">Selecciona…</option>
                <option value="Damas">Damas</option>
                <option value="Varones">Varones</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Distancia *</label>
              <select className={inputClass} required value={form.distancia}
                onChange={(e) => actualizar("distancia", e.target.value)}>
                <option value="">Selecciona…</option>
                {DISTANCIAS.map((d) => (
                  <option key={d.nombre} value={d.nombre}>{d.nombre}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Fecha de nacimiento *</label>
              <input className={inputClass} type="date" required value={form.fechaNacimiento}
                onChange={(e) => actualizar("fechaNacimiento", e.target.value)} />
            </div>
          </div>

          <div>
            <label className={labelClass}>Categoría (asignada automáticamente)</label>
            <input className={`${inputClass} bg-foreground-100 text-foreground-600`} type="text" readOnly
              value={categoria} placeholder="Se calcula según tu fecha de nacimiento" />
            {avisoCategoria && <p className="mt-1 text-sm text-red-700">{avisoCategoria}</p>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Talla de camiseta *</label>
              <select className={inputClass} required value={form.tallaCamiseta}
                onChange={(e) => actualizar("tallaCamiseta", e.target.value)}>
                <option value="">Selecciona…</option>
                {TALLAS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>WhatsApp / Teléfono *</label>
              <input className={inputClass} type="tel" required value={form.telefono}
                onChange={(e) => actualizar("telefono", e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Correo electrónico *</label>
              <input className={inputClass} type="email" required value={form.email}
                onChange={(e) => actualizar("email", e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Club o provincia a la que representa *</label>
              <input className={inputClass} type="text" required value={form.clubProvincia}
                onChange={(e) => actualizar("clubProvincia", e.target.value)} />
            </div>
          </div>

          {/* Datos para el pago (justo antes de subir el comprobante) */}
          <div className="bg-background-50 border border-foreground-100 rounded-2xl p-4 md:p-5">
            <h3 className="font-heading text-xl text-foreground-950 mb-1">DATOS PARA EL PAGO</h3>
            <p className="text-sm text-foreground-600 mb-4">
              Valor de la inscripción: <strong className="text-foreground-900">$20.00</strong>. Realiza el pago y
              adjunta la foto del comprobante aquí abajo.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Transferencias */}
              <div className="space-y-3">
                <div className="bg-white border border-foreground-200 rounded-xl p-4">
                  <p className="font-semibold text-foreground-900">Banco Pichincha</p>
                  <p className="text-sm text-foreground-700">Cuenta de Ahorros: <strong>2200510735</strong></p>
                  <p className="text-sm text-foreground-700">Néstor Quinapanta Vargas</p>
                  <p className="text-sm text-foreground-700">C.I.: 1802347649</p>
                </div>
                <div className="bg-white border border-foreground-200 rounded-xl p-4">
                  <p className="font-semibold text-foreground-900">Banco Guayaquil</p>
                  <p className="text-sm text-foreground-700">Cuenta de Ahorros: <strong>0016549805</strong></p>
                  <p className="text-sm text-foreground-700">Néstor Quinapanta Vargas</p>
                  <p className="text-sm text-foreground-700">C.I.: 1802347649</p>
                  <p className="text-sm text-foreground-700">nestorquinapanta@gmail.com</p>
                </div>
              </div>

              {/* DeUna QR */}
              <div className="bg-white border border-foreground-200 rounded-xl p-4 text-center">
                <p className="font-semibold text-foreground-900 mb-1">Paga con DeUna! (Banco Pichincha)</p>
                <img
                  src="/qr-deuna.PNG"
                  alt="Código QR DeUna para pago"
                  className="mx-auto w-48 h-48 object-contain"
                />
                <p className="text-xs text-foreground-500 mt-1">Nestor Estuardo Quinapanta Vargas</p>
                <ol className="text-left text-sm text-foreground-700 mt-3 space-y-1 list-decimal pl-5">
                  <li>Escoge la opción pagar con QR.</li>
                  <li>Escanea el código QR.</li>
                  <li>Ingresa el monto ($20), paga y ¡listo!</li>
                </ol>
              </div>
            </div>
          </div>

          <div>
            <label className={labelClass}>Comprobante de pago (foto) *</label>
            <input key={resetKey} className={inputClass} type="file" accept="image/*" required
              onChange={(e) => setArchivo(e.target.files?.[0] ?? null)} />
            <p className="mt-1 text-xs text-foreground-400">
              Sube la foto o captura del pago (JPG/PNG, máx. {MAX_MB}MB). Sin el comprobante no se puede aprobar tu inscripción.
            </p>
          </div>

          <div>
            <label className={labelClass}>Déjanos alguna duda o sugerencia (opcional)</label>
            <textarea className={inputClass} rows={3} value={form.comentario}
              onChange={(e) => actualizar("comentario", e.target.value)} />
          </div>

          <button type="submit" disabled={enviando}
            className="inline-flex items-center justify-center gap-2 w-full px-6 py-3 rounded-full bg-primary-500 text-white font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap">
            {enviando ? "ENVIANDO…" : "ENVIAR INSCRIPCIÓN"}
            <i className="ri-arrow-right-line" />
          </button>
        </form>

        {/* Contacto */}
        <section className={`${cardClass} text-center`}>
          <p className="text-sm text-foreground-600">¿Necesitas ayuda con tu inscripción? Escríbenos:</p>
          <p className="font-semibold text-foreground-900 mt-1">Néstor Quinapanta</p>
          <div className="flex items-center justify-center gap-3 mt-3">
            <a href="https://wa.me/593979102532" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-600 text-white text-sm font-semibold hover:bg-green-700">
              <i className="ri-whatsapp-line" /> +593 97 910 2532
            </a>
            <a href="mailto:nestorquinapanta@gmail.com"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-foreground-200 text-foreground-800 text-sm font-semibold hover:bg-foreground-300">
              <i className="ri-mail-line" /> Correo
            </a>
          </div>
        </section>
      </main>
    </div>
  );
}
